import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useParams, useNavigate, useLocation, matchPath } from "react-router-dom";

import ActionButton from "../../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import GuardianEprDao from "../../../dao/GuardianEprDao";
import Icon from "../../icon/Icon";
import LoadingSvg from "../../loadingSvg/LoadingSvg";
import MostRecentEprs from "../../guardian/MostRecentEprs";
import RatingsList from "./Ratings/RatingsList";
import FluencyWpmField from "./Ratings/FluencyWpmField";
import Resources from "../../resources/Resources";
import OverallComments from "./CommentsPanel/OverallComments";
import SleComment from "./CommentsPanel/SleComment";
import StudentDiffModal from "../studentDifferentiation/StudentDiffModal";
import StudentInfoHeader from "./StudentInfoHeader";
import SubjectComments from "./CommentsPanel/SubjectComments";
import SubjectTabs from "./SubjectTabs";
import useFlattenedStudents from "../../../hooks/useFlattenedStudents";
import usePreviousSemesterRatings from "../../../hooks/usePreviousSemesterRatings";
import useSharePermissions from "../../../hooks/useSharePermissions";
import useSharedGroupData from "../../../hooks/useSharedGroupData";
import useSleRatings from "../../../hooks/useSleRatings";
import useStudentData from "../../../hooks/useStudentData";
import isEprEditable from "../../../utils/ActionControls/isEprEditable";
import useStudentImages from "../../../hooks/useStudentImages";
import useStudentRatings from "../../../hooks/useStudentRatings";
import useFluencyWpm from "../../../hooks/useFluencyWpm";
import { enrichEprGradingCriteriaWithFluency, getReadingFluencyFromCustomData } from "../../../const/fluencyCustomData";
import {
    getItemStem,
    isFluencyEligible,
    resolveFluencyGradeName,
    sanitizeBenchmarkValue
} from "../../../utils/fluencyWpm/stems";
import useSubjectSelection from "../../../hooks/useSubjectSelection";
import useSubjectUrlSync from "../../../hooks/useSubjectUrlSync";
import useStudentDifferentiation from "../../../hooks/useStudentDifferentiation";

import { useEnsureSchoolTemplatesForLocation } from "../../contextProvider/ContextSetter";
import { GlobalContext } from "../../contextProvider/ContextProvider";

import "../../../styles/GuardianHome.scss";
import "../../../styles/RatingTable.scss";
import "../../../styles/SingleStudentRatings.scss";

const SingleStudentRatings = ({ dispatch, state, basePath, overrideTeacherGuid }) => {
    const { gradingProfiles, selectedEpr, navCollapsed } = state;
    const { state: globalState } = useContext(GlobalContext);
    const {
        actionWindowsDto,
        currentTimePeriodDto,
        schoolLevelTemplateDto,
        schoolTemplatesByLocation,
        schoolYearDto,
        timePeriodDto,
        token,
        userDetails
    } = globalState || {};

    const ensureTemplatesForLocation = useEnsureSchoolTemplatesForLocation();

    const navigate = useNavigate();
    const location = useLocation();
    const { studentNumber: paramStudentNumber } = useParams();

    const studentNumber = useMemo(() => {
        if (paramStudentNumber) return paramStudentNumber;
        const match = matchPath(`${basePath || ""}/summary/input-ratings/student/:id/*`, location.pathname);
        const matchedId = match?.params?.id;
        return matchedId && !Number.isNaN(Number(matchedId)) ? matchedId : null;
    }, [paramStudentNumber, location.pathname, basePath]);

    const navState = location.state || {};
    const navSelectedTemplateId = location.state?.selectedTemplateId;
    const navMode = location.state?.mode;
    const navGradingCriteriaId = location.state?.gradingCriteriaId;

    const isGroupsNav = navMode === "GROUPS" || navMode === "GROUPS_SLE";

    const view = isGroupsNav ? "GROUPS" : "HOMEROOM";

    const incomingTemplateId = useMemo(() => {
        const fromState = navSelectedTemplateId;
        const fromQuery = new URLSearchParams(location.search || "").get("templateId");

        if (fromQuery === "SLE" || fromState === "SLE") {
            return "SLE";
        }

        const value = fromState ?? fromQuery;
        if (!value) return null;

        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    }, [navSelectedTemplateId, location.search]);

    const [loader, setLoader] = useState(false);
    const [previewShow, setPreviewShow] = useState(false);
    const [previewProfileData, setPreviewProfileData] = useState(null);
    /** Saved fluency for subjects not visited this session (e.g. refresh while on Math). */
    const [previewSavedFluency, setPreviewSavedFluency] = useState({});
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewFetchIdRef = useRef(0);
    const [semester1ViewShow, setSemester1ViewShow] = useState(false);
    const [semester1EprData, setSemester1EprData] = useState(null);
    const [profileComment, setProfileComment] = useState("");
    const [selectedSubjectId, setSelectedSubjectId] = useState(incomingTemplateId);
    const [show, setShow] = useState(false);
    const [subjectComment, setSubjectComment] = useState("");

    // Use shared group data hook
    const {
        allSharedGroups,
        sharedGroup,
        selectedGradeForGroups,
        combinedGrades,
        sharedGroupGradingProfileDto,
        sharedGroupPermission
    } = useSharedGroupData({
        view,
        navState,
        token,
        userDetails,
        schoolYearDto,
        currentTimePeriodDto,
        incomingTemplateId,
        overrideTeacherGuid,
        studentNumber
    });

    const homeroomGroupFromEpr = useMemo(() => {
        if (!isGroupsNav) return null;
        if (!navState?.sharedGroupId) return null;

        return selectedEpr?.homeroomGroups?.find((g) => String(g.key) === String(navState.sharedGroupId)) ?? null;
    }, [isGroupsNav, navState?.sharedGroupId, selectedEpr?.homeroomGroups]);

    const groupKind = useMemo(() => {
        if (!isGroupsNav) return "HOMEROOM";
        if (sharedGroup) return "SHARED_GROUP";
        if (homeroomGroupFromEpr) return "HOMEROOM_GROUP";
        return "UNKNOWN_GROUP";
    }, [isGroupsNav, sharedGroup, homeroomGroupFromEpr]);

    const flattened = useFlattenedStudents({
        mode: "HOMEROOM",
        groupData: null,
        selectedEpr,
        existingRatingsData: null
    });

    // Use student data hook
    const { student, studentProfileData, gradingProfileDto, isActiveWindow } = useStudentData({
        view,
        sharedGroup,
        studentNumber,
        flattened,
        selectedEpr,
        token,
        actionWindowsDto
    });

    const templateLocationKey = useMemo(() => {
        if (groupKind === "SHARED_GROUP") return sharedGroup?.locationKey ?? null;
        return studentProfileData?.locationId ?? null; // HOMEROOM + HOMEROOM_GROUP
    }, [groupKind, sharedGroup?.locationKey, studentProfileData?.locationId]);

    const schoolLevelTemplateOverride = useMemo(() => {
        if (!templateLocationKey) return null;
        return schoolTemplatesByLocation?.[templateLocationKey] ?? null;
    }, [templateLocationKey, schoolTemplatesByLocation]);

    const { subjectOptions, selectedTemplate, setSelectedTemplate } = useSubjectSelection({
        selectedEpr,
        schoolLevelTemplateDto,
        schoolLevelTemplateOverride,
        incomingTemplateId,
        currentTimePeriodName: currentTimePeriodDto?.name,
        view,
        selectedGrade: selectedGradeForGroups,
        combinedGrades
    });

    const effectiveGradingCriteriaId = useMemo(() => {
        if (navGradingCriteriaId) return navGradingCriteriaId;
        if (!incomingTemplateId || incomingTemplateId === "SLE") return null;

        if (groupKind === "SHARED_GROUP") {
            const list = sharedGroupGradingProfileDto?.gradingCriteriaList ?? [];
            const match = list.find((c) => String(c.parentTemplate) === String(incomingTemplateId));
            return match?.key ?? null;
        }

        if (groupKind === "HOMEROOM_GROUP" || groupKind === "HOMEROOM") {
            const list = selectedEpr?.gradingCriteriaList ?? [];
            const match = list.find((c) => String(c.parentTemplate) === String(incomingTemplateId));
            return match?.key ?? null;
        }

        return null;
    }, [
        navGradingCriteriaId,
        incomingTemplateId,
        groupKind,
        sharedGroupGradingProfileDto,
        selectedEpr?.gradingCriteriaList
    ]);

    // Use student differentiation hook
    const { diffRatings, reportableContent, setDiffGrades, setDiffRatings } = useStudentDifferentiation({
        student,
        selectedTemplate,
        selectedEpr,
        navMode,
        effectiveGradingCriteriaId,
        token
    });

    const derivedSelectedGroupContent = useMemo(() => {
        if (view !== "GROUPS") return null;

        if (sharedGroup) {
            return [
                {
                    groupId: sharedGroup.sharedGroupId,
                    groupName: sharedGroup.sharedGroupName,
                    teacherName: sharedGroup.sharedGroupCreatorDisplayName,
                    gradingProfileId: sharedGroup.sharedGradingProfile,
                    groupPermission: sharedGroupPermission,
                    groupShareDto: sharedGroup.groupShareDto,
                    groupSleShareDto: sharedGroup.groupSleShareDto,
                    groupStudentContents: (sharedGroup.sharedGroupStudents || []).map((s) => ({
                        studentNumber: s.studentNumber,
                        gradingProfileStudentId: s.gradingProfileStudentId,
                        sectionId: s.sectionId,
                        firstName: s.firstName,
                        lastName: s.lastName
                    }))
                }
            ];
        }

        if (homeroomGroupFromEpr) {
            return [
                {
                    groupId: homeroomGroupFromEpr.key,
                    groupName: homeroomGroupFromEpr.name,
                    groupType: "HOMEROOM",
                    groupPermission: "EDIT",
                    groupStudentContents: (homeroomGroupFromEpr.homeroomGroupStudents || []).map((s) => ({
                        studentNumber: s.studentNumber,
                        firstName: s.firstName,
                        lastName: s.lastName
                    }))
                }
            ];
        }

        return null;
    }, [view, sharedGroup, sharedGroupPermission, homeroomGroupFromEpr]);

    // stable sorted list for header switcher
    const sortedHomeroomStudents = useMemo(() => {
        const list = Array.isArray(flattened) ? [...flattened] : [];
        return list.sort((a, b) => {
            const lastA = (a.lastName || "").toLowerCase();
            const lastB = (b.lastName || "").toLowerCase();
            if (lastA !== lastB) return lastA.localeCompare(lastB);
            const firstA = (a.firstName || "").toLowerCase();
            const firstB = (b.firstName || "").toLowerCase();
            return firstA.localeCompare(firstB);
        });
    }, [flattened]);

    const sortedGroupStudents = useMemo(() => {
        let list = [];

        if (groupKind === "SHARED_GROUP") {
            list = Array.isArray(sharedGroup?.sharedGroupStudents) ? [...sharedGroup.sharedGroupStudents] : [];
        } else if (groupKind === "HOMEROOM_GROUP") {
            const gpsByStudentNumber = new Map(
                (selectedEpr?.gradingProfileStudents ?? []).map((s) => [String(s.studentNumber), s])
            );

            list = (homeroomGroupFromEpr?.homeroomGroupStudents ?? []).map((s) => {
                const gps = gpsByStudentNumber.get(String(s.studentNumber));
                return {
                    ...s,
                    gradingProfileStudentId: gps?.key, // if you need it later
                    key: gps?.key ?? s.key, // stable key
                    gradingProfileStatus: selectedEpr?.status,
                    isShared: true // keeps GROUPS styling consistent if you rely on it
                };
            });
        }

        return list.sort((a, b) => {
            const lastA = (a.lastName || "").toLowerCase();
            const lastB = (b.lastName || "").toLowerCase();
            if (lastA !== lastB) return lastA.localeCompare(lastB);
            const firstA = (a.firstName || "").toLowerCase();
            const firstB = (b.firstName || "").toLowerCase();
            return firstA.localeCompare(firstB);
        });
    }, [
        groupKind,
        sharedGroup?.sharedGroupStudents,
        homeroomGroupFromEpr?.homeroomGroupStudents,
        selectedEpr?.gradingProfileStudents,
        selectedEpr?.status
    ]);

    const currentStudents = view === "GROUPS" ? sortedGroupStudents : sortedHomeroomStudents;

    const currentStudentIndexEffective = useMemo(() => {
        if (!studentNumber) return -1;
        return currentStudents.findIndex((s) => String(s.studentNumber) === String(studentNumber));
    }, [currentStudents, studentNumber]);

    const buildStudentUrl = useCallback(
        (nextStudentNumber) => {
            const params = new URLSearchParams(location.search || "");

            if (incomingTemplateId) {
                params.set("templateId", String(incomingTemplateId));
            } else {
                params.delete("templateId");
            }

            const qs = params.toString();
            const queryString = qs ? `?${qs}` : "";
            return `${basePath || "/home"}/summary/input-ratings/student/${nextStudentNumber}${queryString}`;
        },
        [incomingTemplateId, location.search, basePath]
    );

    const {
        loading: sleLoading,
        sortedSles,
        sleValuesByKey,
        onChangeSleRatingByKey,
        saveSleRatings,
        sleTotalCount,
        sleRatedCount,
        sleComment,
        setSleComment,
        saveSleComment
    } = useSleRatings({
        incomingTemplateId,
        student,
        studentProfileData,
        userGuid: userDetails?.uid,
        token
    });

    const { canEdit, isOwned } = useSharePermissions({
        student,
        studentProfileData,
        selectedTemplate,
        userDetails,
        view,
        gradingProfiles,
        selectedGroupContent: derivedSelectedGroupContent,
        groupPermissionField: "groupPermission",
        currentTeacherName: userDetails?.displayName,
        navMode,
        incomingTemplateId
    });

    const { setUrlTemplateId } = useSubjectUrlSync();
    const {
        contentItems,
        totalContentItems,
        totalDiffRatings,
        totalRatingsCombined,
        totalCommentsCombined,
        diffItemsByGrade,
        valuesByKey,
        commentsByKey,

        existingRatingsData,
        commentVisible,
        studentOverallComment,
        setStudentOverallComment,
        studentSubjectComment,
        setStudentSubjectComment,
        studentReadingFluency,
        setStudentReadingFluency,
        hasChanges,
        toggleCommentVisible,
        onChangeRatingByKey,
        onChangeCommentByKey,

        saveStudentSubjectComment,
        saveStudentOverallComment,
        saveRatings,
        refetchRatings,
        sharedStudentsGradingProfileData
    } = useStudentRatings({
        student,
        studentProfileData,
        selectedEpr,
        selectedTemplate,
        token,
        userGuid: userDetails?.uid,
        view,
        gradingCriteriaId: effectiveGradingCriteriaId,
        reportableContent,
        diffRatings,
        setDiffRatings,
        setDiffGrades
    });

    const fluencyGradeName = resolveFluencyGradeName({
        studentGradeName: studentProfileData?.gradeName,
        selectedGrade: selectedGradeForGroups,
        sharedGradeName: sharedGroup?.sharedGradeName,
        eprGradeName: selectedEpr?.gradeName
    });
    const { items: fluencyItems } = useFluencyWpm({
        gradeName: fluencyGradeName,
        subjectName: selectedTemplate?.subjectName
    });

    const previewEprs = useMemo(() => {
        if (!previewProfileData) return [];

        // Preview is saved-only: use fluency loaded at preview open, not session/UI edits.
        return [enrichEprGradingCriteriaWithFluency(previewProfileData, {}, previewSavedFluency)];
    }, [previewProfileData, previewSavedFluency]);

    const handlePreviewShow = useCallback(
        (show) => {
            if (!show) {
                previewFetchIdRef.current += 1;
                setPreviewShow(false);
                setPreviewProfileData(null);
                setPreviewSavedFluency({});
                setPreviewLoading(false);
                return;
            }

            const fetchId = ++previewFetchIdRef.current;
            setPreviewProfileData(null);
            setPreviewSavedFluency({});
            setPreviewShow(true);

            const gradingProfileStudentId = student?.gradingProfileStudentId;
            if (!gradingProfileStudentId || !token) {
                setPreviewLoading(false);
                toast.error("Unable to load Report Card preview.");
                return;
            }

            const criteriaList =
                selectedEpr?.gradingCriteriaList ??
                gradingProfileDto?.gradingCriteriaList ??
                sharedGroupGradingProfileDto?.gradingCriteriaList ??
                [];

            const fluencyCriteria = (subjectOptions || [])
                .filter((s) => s?.subjectName && isFluencyEligible(fluencyGradeName, s.subjectName))
                .map((s) => {
                    const criteria = criteriaList.find((c) => String(c.parentTemplate) === String(s.templateId));
                    return criteria?.key != null
                        ? { subjectName: s.subjectName, gradingCriteriaId: criteria.key }
                        : null;
                })
                .filter(Boolean);

            setPreviewLoading(true);

            const profilePromise = ElementaryProgressReportDao({
                action: "getStudentProfileData",
                gradingProfileStudentId,
                token
            });

            const fluencyPromise = Promise.all(
                fluencyCriteria.map(({ subjectName, gradingCriteriaId }) =>
                    ElementaryProgressReportDao({
                        action: "getStudentRatings",
                        gradingProfileStudentId,
                        gradingCriteriaId,
                        token
                    })
                        .then((res) => {
                            const wpm = getReadingFluencyFromCustomData(
                                res?.data?.payload?.contentStudentComments?.customData
                            );
                            // Include empty so a saved clear can wipe stale customData on the profile DTO.
                            return { subjectName, wpm };
                        })
                        .catch(() => null)
                )
            );

            Promise.all([profilePromise, fluencyPromise])
                .then(([profileResponse, fluencyRows]) => {
                    if (previewFetchIdRef.current !== fetchId) return;
                    const dto = profileResponse?.data?.payload;
                    if (!dto) {
                        toast.error("Unable to load Report Card preview.");
                        return;
                    }
                    const saved = {};
                    (fluencyRows || []).forEach((row) => {
                        if (row?.subjectName) saved[row.subjectName] = row.wpm ?? "";
                    });
                    setPreviewSavedFluency(saved);
                    setPreviewProfileData(dto);
                })
                .catch((error) => {
                    if (previewFetchIdRef.current !== fetchId) return;
                    console.error(error);
                    toast.error("Unable to load Report Card preview.");
                })
                .finally(() => {
                    if (previewFetchIdRef.current === fetchId) setPreviewLoading(false);
                });
        },
        [
            student?.gradingProfileStudentId,
            token,
            selectedEpr?.gradingCriteriaList,
            gradingProfileDto?.gradingCriteriaList,
            sharedGroupGradingProfileDto?.gradingCriteriaList,
            subjectOptions,
            fluencyGradeName
        ]
    );

    const { previousRatingsByBodyText, previousDiffRatingsByContentId, previousSleRatingsByKey, hasPreviousSemester } =
        usePreviousSemesterRatings({
            student,
            selectedTemplate,
            currentTimePeriodDto,
            timePeriodDto,
            gradingProfiles,
            schoolLevelTemplateDto,
            schoolYearDto,
            sharedGroup,
            token,
            schoolTemplatesByLocation
        });

    const schoolYearKeyForImages =
        selectedEpr?.schoolYearKey ||
        sharedStudentsGradingProfileData?.schoolYearKey ||
        studentProfileData?.schoolYearKey ||
        schoolYearDto?.key;

    const { images: studentImages } = useStudentImages(student ? [student] : [], schoolYearKeyForImages, token);

    const getStudentImageElement = () => {
        if (!student) return null;

        if (studentImages[student.studentNumber]) {
            return (
                <img
                    src={studentImages[student.studentNumber]}
                    alt={`${student.firstName} ${student.lastName}`}
                    style={{ width: 90, height: 120, borderRadius: "6%", objectFit: "cover" }}
                />
            );
        }

        return <Icon iconName="NO_STUDENT_IMAGE" fill="#19608F" height={120} width={90} />;
    };

    const imageEl = getStudentImageElement();

    const findSemester1Profile = useCallback(() => {
        if (!student || !timePeriodDto || !gradingProfiles) return null;

        const firstSemester = timePeriodDto.find((tp) => tp.name === "1st Semester");
        if (!firstSemester) return null;

        return gradingProfiles.find((gp) => {
            const matchesTimePeriod = Number(gp.timePeriod) === Number(firstSemester.key);
            if (!matchesTimePeriod) return false;
            return gp.gradingProfileStudents?.some((s) => String(s.studentNumber) === String(student.studentNumber));
        });
    }, [student, timePeriodDto, gradingProfiles]);

    const fetchSemester1EprData = useCallback(async () => {
        if (!student || !schoolYearDto?.key || !token) return;

        try {
            const options = {
                action: "eprGradingProfileRead",
                schoolYearKey: schoolYearDto.key,
                studentNumber: student.studentNumber,
                token
            };

            const response = await GuardianEprDao(options);
            if (response?.data?.payload) {
                const eprs = response.data.payload;
                const semester1Epr = eprs?.find((epr) => epr.semester === "1st Semester");
                if (semester1Epr) {
                    setSemester1EprData(semester1Epr);
                }
            }
        } catch (error) {
            console.error("Error fetching semester 1 EPR data:", error);
            toast.error("Unable to load 1st Semester data.");
        }
    }, [student, schoolYearDto, token]);

    // Fetch semester 1 EPR data when modal is opened
    useEffect(() => {
        if (!semester1ViewShow || !hasPreviousSemester) {
            return;
        }

        const semester1Profile = findSemester1Profile();
        if (!semester1Profile) return;

        fetchSemester1EprData();
    }, [semester1ViewShow, hasPreviousSemester, findSemester1Profile, fetchSemester1EprData]);

    const handleSubjectSelect = useCallback(
        async (subject) => {
            if (hasChanges) {
                try {
                    await saveRatings();
                } catch (err) {
                    console.error("Failed to save before subject change:", err);
                }
            }

            if (subject === "SLE") {
                setUrlTemplateId("SLE", true);
                setSelectedSubjectId("SLE");
                setSelectedTemplate(null);
                return;
            }
            setSelectedTemplate(subject);
            setSelectedSubjectId(subject.templateId);
            setUrlTemplateId(subject.templateId, true);
        },
        [hasChanges, saveRatings, setSelectedTemplate, setSelectedSubjectId, setUrlTemplateId]
    );

    const downloadPdf = async () => {
        try {
            const options = {
                action: "eprPdfDownload",
                gradingProfileId: studentProfileData.gradingProfileId,
                studentNumber: studentProfileData.studentNumber,
                token
            };
            setLoader(true);
            const nameArray = studentProfileData.studentName?.split(", ");
            const lastName = nameArray[0] || "";
            const firstName = nameArray[1] || "student";
            const response = await GuardianEprDao(options);
            if (response) {
                // This will open the PDF in a new tab AND download the file simultaneously
                const url = globalThis.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `EPR_${lastName}_${firstName}_${studentProfileData?.studentNumber}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                globalThis.URL.revokeObjectURL(url);

                toast.success("Report Card pdf downloaded.", {
                    autoClose: 3000
                });
            }
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                toast.error("Unable to download Report Card.");
            }
        } finally {
            setLoader(false);
        }
    };

    const downloadSemester1Pdf = useCallback(async () => {
        if (!semester1EprData) return;

        try {
            const options = {
                action: "eprPdfDownload",
                gradingProfileId: semester1EprData.gradingProfileId,
                studentNumber: semester1EprData.studentNumber,
                token
            };
            setLoader(true);
            const nameArray = semester1EprData.studentName?.split(", ");
            const lastName = nameArray[0] || "";
            const firstName = nameArray[1] || "student";
            const response = await GuardianEprDao(options);
            if (response) {
                const url = globalThis.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `EPR_Sem1_${lastName}_${firstName}_${semester1EprData?.studentNumber}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                globalThis.URL.revokeObjectURL(url);
                toast.success("1st Semester Report Card pdf downloaded.", { autoClose: 3000 });
            }
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                toast.error("Unable to download 1st Semester Report Card.");
            }
        } finally {
            setLoader(false);
        }
    }, [semester1EprData, token]);

    const groupSubjectOptions = useMemo(() => {
        if (view !== "GROUPS") return [];
        if (!sharedGroup || !sharedGroupGradingProfileDto) return [];
        const criteriaList = sharedGroupGradingProfileDto.gradingCriteriaList ?? [];
        const shares = sharedGroup.groupShareDto?.gradingCriteriaShares ?? [];

        const permByTemplateId = new Map();
        for (const c of criteriaList) {
            const templateId = c.parentTemplate;
            const criteriaId = c.key;

            const matchingShares = shares.filter((s) => String(s.gradingCriteriaId) === String(criteriaId));

            if (matchingShares.some((s) => s.permission === "EDIT")) permByTemplateId.set(String(templateId), "EDIT");
            else if (matchingShares.some((s) => s.permission === "VIEW"))
                permByTemplateId.set(String(templateId), "VIEW");
        }

        return (subjectOptions ?? [])
            .filter((opt) => permByTemplateId.has(String(opt.templateId)))
            .map((opt) => ({ ...opt, permission: permByTemplateId.get(String(opt.templateId)) }));
    }, [view, sharedGroup, sharedGroupGradingProfileDto, subjectOptions]);

    const effectiveSubjectOptions = groupKind === "SHARED_GROUP" ? groupSubjectOptions : subjectOptions;

    useEffect(() => {
        if (!allSharedGroups || allSharedGroups.length === 0) return;

        const uniqueLocationKeys = [...new Set(allSharedGroups.map((g) => g.locationKey).filter(Boolean))];

        uniqueLocationKeys.forEach((locationKey) => {
            ensureTemplatesForLocation({ locationKey });
        });
    }, [allSharedGroups, ensureTemplatesForLocation]);

    /**
     * If there is NO templateId in the URL, default to first subject option once loaded.
     */
    useEffect(() => {
        if (incomingTemplateId) return; // URL already drives selection (or SLE)
        if (!effectiveSubjectOptions || effectiveSubjectOptions.length === 0) return;

        const first = effectiveSubjectOptions[0];
        setSelectedTemplate(first);
        setSelectedSubjectId(first.templateId);
        setUrlTemplateId(first.templateId, true); // writes ?templateId=...
    }, [incomingTemplateId, effectiveSubjectOptions, setSelectedTemplate, setSelectedSubjectId, setUrlTemplateId]);

    useEffect(() => {
        if (!incomingTemplateId) return;
        setSelectedSubjectId(incomingTemplateId);
    }, [incomingTemplateId]);

    useEffect(() => {
        setProfileComment(selectedEpr?.comment || "");
    }, [selectedEpr?.comment]);

    // Restore selectedEpr on refresh by locating the student's EPR
    useEffect(() => {
        if (!studentNumber || !Array.isArray(gradingProfiles) || gradingProfiles.length === 0) return;

        const gradingProfilesCurrentSem = (gradingProfiles || []).filter(
            (p) => Number(p.timePeriod) === Number(currentTimePeriodDto?.key)
        );
        const eprForStudent = gradingProfilesCurrentSem.find((gp) =>
            gp?.gradingProfileStudents?.some((s) => String(s.studentNumber) === String(studentNumber))
        );

        if (eprForStudent && (!selectedEpr || String(selectedEpr.key) !== String(eprForStudent.key))) {
            dispatch({ type: "SET_SELECTED_EPR", payload: eprForStudent });
        }
    }, [currentTimePeriodDto, studentNumber, gradingProfiles, selectedEpr, dispatch]);

    useEffect(() => {
        if (!selectedTemplate) {
            setSubjectComment("");
            return;
        }
        const profileToUse = selectedEpr?.gradingCriteriaList ? selectedEpr : gradingProfileDto;

        if (!profileToUse) return;
        const crit = (profileToUse.gradingCriteriaList || []).find(
            (c) => Number(c.parentTemplate) === Number(selectedTemplate.templateId)
        );
        setSubjectComment(crit?.comment || "");
    }, [selectedTemplate, selectedEpr, gradingProfileDto]);

    const isActiveAndIsUnreleased = isEprEditable({
        isActiveWindow,
        status: gradingProfileDto?.status,
        userDetails
    });
    const isSle = incomingTemplateId === "SLE";
    const isSharedGroupKind = groupKind === "SHARED_GROUP";

    let sleTabsEnabled = true;
    let slePermission = "EDIT";

    if (view === "GROUPS" && isSharedGroupKind) {
        const perm = sharedGroup?.groupSleShareDto?.permission;
        sleTabsEnabled = Boolean(perm);
        slePermission = perm ?? "VIEW";
    }

    const canEditSle = slePermission === "EDIT";
    const canEditEffective = isSle ? canEditSle : canEdit;
    const groupLabel = useMemo(() => {
        if (view !== "GROUPS") return null;
        if (groupKind === "SHARED_GROUP") {
            const creator = sharedGroup?.sharedGroupCreatorDisplayName ?? "";
            const rawName = sharedGroup?.sharedGroupName ?? "";
            const cleaned = rawName.replace(/^INTERNAL_/, "").replaceAll("_", " ");
            return `${creator}${creator && cleaned ? " - " : ""}${cleaned}`.trim() || null;
        }

        if (groupKind === "HOMEROOM_GROUP") {
            const rawName = homeroomGroupFromEpr?.name ?? "";
            return rawName.replace(/^INTERNAL_/, "").replaceAll("_", " ") || null;
        }

        return null;
    }, [
        view,
        groupKind,
        sharedGroup?.sharedGroupCreatorDisplayName,
        sharedGroup?.sharedGroupName,
        homeroomGroupFromEpr?.name
    ]);
    if (!student) {
        return (
            <div className="gutter-95" style={{ padding: 24 }}>
                <p>Student not found.</p>
            </div>
        );
    }

    return (
        <div className="right-side-wrapper" style={navCollapsed ? { maxWidth: "95%" } : { maxWidth: "80%" }}>
            <StudentInfoHeader
                basePath={basePath}
                buildStudentUrl={buildStudentUrl}
                canEdit={canEditEffective}
                currentStudentIndex={currentStudentIndexEffective}
                currentTimePeriodDto={currentTimePeriodDto}
                currentTimePeriodName={currentTimePeriodDto?.name}
                downloadPdf={downloadPdf}
                gradingProfileStatus={gradingProfileDto?.status}
                groupLabel={groupLabel}
                imageEl={imageEl}
                incomingTemplateId={incomingTemplateId}
                isOwned={isOwned}
                loader={loader}
                location={location}
                navigate={navigate}
                navState={navState}
                schoolYearName={schoolYearDto?.name}
                selectedEpr={selectedEpr}
                setPreviewShow={handlePreviewShow}
                setSemester1ViewShow={setSemester1ViewShow}
                student={student}
                studentProfileData={studentProfileData}
                students={currentStudents}
                view={view}
            />
            {loader && <LoadingSvg />}
            {studentProfileData && (
                <div className="mt-3">
                    <Resources
                        canEdit={canEditEffective}
                        gradingProfileId={studentProfileData?.gradingProfileId}
                        gradingProfileStudentId={student.gradingProfileStudentId}
                        initialResources={studentProfileData.externalResources || []}
                        isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                        schoolYearKey={studentProfileData?.schoolYearKey}
                        sectionId={studentProfileData.sectionId}
                        studentNumber={student.studentNumber}
                        studentOverallComment={studentOverallComment}
                        token={token}
                    />
                </div>
            )}
            <div className="comments-and-tabs">
                {isOwned && (
                    <OverallComments
                        currentTimePeriodDto={currentTimePeriodDto}
                        isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                        profileComment={profileComment}
                        saveStudentOverallComment={saveStudentOverallComment}
                        setStudentOverallComment={setStudentOverallComment}
                        student={student}
                        studentProfileData={studentProfileData}
                        studentOverallComment={studentOverallComment}
                    />
                )}

                <div className="subject-main-section ">
                    <SubjectTabs
                        onSelect={handleSubjectSelect}
                        selectedSubjectId={selectedSubjectId}
                        schoolLevelExpectations={sortedSles}
                        sleTabsEnabled={sleTabsEnabled}
                        subjectOptions={effectiveSubjectOptions}
                        view={view}
                    />

                    {selectedTemplate && (
                        <div style={{ margin: "24px -10px" }}>
                            <ActionButton
                                disable={!isActiveAndIsUnreleased || !canEdit}
                                label={`Differentiate ${selectedTemplate?.subjectName}`}
                                onClick={() => setShow(true)}
                            />
                        </div>
                    )}

                    {/* Subject Comments Section */}
                    {selectedTemplate && (
                        <SubjectComments
                            canEdit={canEditEffective && isActiveAndIsUnreleased}
                            isOwned={isOwned}
                            onSave={saveStudentSubjectComment}
                            selectedTemplate={selectedTemplate}
                            setValue={setStudentSubjectComment}
                            student={student}
                            subjectComment={subjectComment}
                            value={studentSubjectComment}
                        />
                    )}

                    {/* Subject Comments Section */}
                    {incomingTemplateId === "SLE" && (
                        <SleComment
                            canEdit={canEditSle && isActiveAndIsUnreleased}
                            classSleComment={gradingProfileDto?.sleComment}
                            isOwned={isOwned}
                            loading={sleLoading}
                            onSave={saveSleComment}
                            setValue={setSleComment}
                            student={student}
                            value={sleComment}
                        />
                    )}

                    {/* Ratings Section */}
                    <section className="section-card">
                        <header className="section-card__header">
                            <h5 className="section-card__title">
                                {incomingTemplateId === "SLE"
                                    ? "School Level Expectations"
                                    : selectedTemplate?.subjectName || ""}{" "}
                                Ratings
                            </h5>
                            <div>
                                {incomingTemplateId === "SLE" ? (
                                    <div>
                                        {sleRatedCount} of {sleTotalCount} Expectations Rated
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            {totalRatingsCombined} of {totalContentItems + totalDiffRatings} Items Rated
                                        </div>
                                        <div>{totalCommentsCombined} Content Comment(s)</div>
                                    </>
                                )}
                            </div>
                        </header>
                        <div className="section-card__body">
                            {!isSle &&
                                fluencyItems.map((item) => (
                                    <FluencyWpmField
                                        canEdit={canEditEffective && isActiveAndIsUnreleased}
                                        inputLabel={item.inputLabel}
                                        key={item.id}
                                        label={item.label}
                                        maxLength={item.maxLength}
                                        onChange={(next) =>
                                            setStudentReadingFluency(sanitizeBenchmarkValue(item, next))
                                        }
                                        placeholder={item.placeholder}
                                        stem={getItemStem(item, fluencyGradeName)}
                                        teacherGuidance={item.teacherGuidance}
                                        value={studentReadingFluency}
                                    />
                                ))}
                            <RatingsList
                                canEdit={canEditEffective}
                                contentItems={contentItems}
                                diffItemsByGrade={diffItemsByGrade}
                                incomingTemplateId={incomingTemplateId}
                                isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                                valuesByKey={valuesByKey}
                                commentsByKey={commentsByKey}
                                onChangeRatingByKey={onChangeRatingByKey}
                                commentVisible={commentVisible}
                                onChangeCommentByKey={onChangeCommentByKey}
                                onToggleComment={toggleCommentVisible}
                                schoolLevelExpectations={sortedSles}
                                studentProfileDataGrade={studentProfileData?.gradeName}
                                sleValuesByKey={sleValuesByKey}
                                onChangeSleRatingByKey={onChangeSleRatingByKey}
                                previousRatingsByBodyText={previousRatingsByBodyText}
                                previousDiffRatingsByContentId={previousDiffRatingsByContentId}
                                previousSleRatingsByKey={previousSleRatingsByKey}
                                hasPreviousSemester={hasPreviousSemester}
                            />
                        </div>
                    </section>
                </div>
            </div>
            {(incomingTemplateId === "SLE" || (contentItems && contentItems.length > 0)) && (
                <div
                    className="save-btn-ratings"
                    style={{
                        background: "white",
                        position: "fixed",
                        paddingBottom: 24,
                        paddingTop: 8,
                        paddingRight: "7%",
                        bottom: 0,
                        right: 0,
                        zIndex: 10
                    }}
                >
                    <ActionButton
                        label="Save"
                        disable={!canEditEffective || !isActiveAndIsUnreleased}
                        onClick={async () => {
                            if (incomingTemplateId === "SLE") {
                                await saveSleRatings();
                                return;
                            }
                            await saveRatings();
                            if (fluencyItems.length > 0) {
                                await saveStudentSubjectComment();
                            }
                        }}
                    />
                </div>
            )}
            <StudentDiffModal
                existingRatingsData={existingRatingsData}
                student={student}
                selectedTemplate={selectedTemplate}
                studentProfileData={studentProfileData}
                show={show}
                setShow={setShow}
                onSaved={(rows) => {
                    // student_diff create/update often returns grade: null for
                    // off-template rows. Keep any grades we have, then refetch
                    // all_ratings (getStudentRatings) for the graded payload.
                    const next = rows || [];
                    const nextGrades = [...new Set(next.map((r) => r.grade).filter(Boolean))].sort((a, b) =>
                        String(a).localeCompare(String(b))
                    );
                    if (next.length) setDiffRatings(next);
                    if (nextGrades.length) setDiffGrades(nextGrades);
                    refetchRatings();
                }}
                onApplyExclusions={(items) => {
                    const overrides = Object.fromEntries(
                        items.map(({ templateContentId }) => [`C:${templateContentId}`, "EXCLUDED"])
                    );
                    // Save using the snapshot (no race with state)
                    saveRatings(overrides, true);
                    return "SWITCH_TO_DIFF";
                }}
            />

            <Modal
                show={previewShow}
                onHide={() => handlePreviewShow(false)}
                size="xl"
                backdrop="static"
                aria-labelledby="epr-preview-modal-title"
            >
                <Modal.Header closeButton>
                    {" "}
                    <Modal.Title id="epr-preview-modal-title" className="visually-hidden">
                        Report Card preview
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="guardian-gutter">
                        {previewShow &&
                            (previewEprs.length > 0 ? (
                                <MostRecentEprs
                                    key={`preview-${previewEprs[0]?.gradingProfileId ?? previewEprs[0]?.gradingProfileStudentId}`}
                                    downloadPdf={downloadPdf}
                                    eprs={previewEprs}
                                    loader={loader || previewLoading}
                                    trackGA={false}
                                    reportableContent={reportableContent}
                                    diffRatings={existingRatingsData[0]?.diffRatings || diffRatings}
                                />
                            ) : (
                                <div className="text-center p-4">
                                    {previewLoading ? <LoadingSvg /> : <p>Unable to load Report Card preview.</p>}
                                </div>
                            ))}
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={semester1ViewShow}
                onHide={() => {
                    setSemester1ViewShow(false);
                    setSemester1EprData(null);
                }}
                size="xl"
                backdrop="static"
                aria-labelledby="semester1-view-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="semester1-view-modal-title">1st Semester Report Card</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="guardian-gutter">
                        {semester1ViewShow &&
                            (semester1EprData ? (
                                <MostRecentEprs
                                    downloadPdf={downloadSemester1Pdf}
                                    eprs={[semester1EprData]}
                                    loader={loader}
                                    trackGA={false}
                                />
                            ) : (
                                <div className="text-center p-4">
                                    <LoadingSvg />
                                    <p>Loading 1st Semester data...</p>
                                </div>
                            ))}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

SingleStudentRatings.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    basePath: PropTypes.string,
    overrideTeacherGuid: PropTypes.string
};

export default SingleStudentRatings;
